import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { uniqueId } from 'lodash';
import { useNavigate } from 'react-router-dom';
import useGetProfile from './useGetProfile';
import { IComparableProperty } from '@/components/ComparableSales';

const ENDPOINT = import.meta.env.DEV
  ? 'http://localhost:8080'
  : 'https://property-tax-api.yourwayhome.com';

interface Payload {
  comparableSales: IComparableProperty[];
  address: string;
  name: string;
  emailAddress: string;
  phoneNumber: string;
  apn?: string;
  state: string;
  fairMarketValue?: string;
  officialValuation?: string;
  legalDescription?: string;
  accountId?: string;
  assessor: {
    countyName: string;
    address: string;
    phone?: string;
    websiteUrl?: string;
    protestUrl?: string;
  };
  aiComparableSales?: IComparableProperty[];
  yearBuilt?: string;
  soldPrice?: number;
  images?: string[];
}

export default function useCreateReport() {
  const queryClient = useQueryClient();
  const navigation = useNavigate();
  const { data: profile } = useGetProfile();

  const token = window.localStorage.getItem('_token');

  return useMutation({
    mutationFn: async (payload: Payload) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(['REPORTS'], (prev: any) => {
        const newReport = {
          accountId: profile.auth0Id,
          address: payload.address,
          createdAt: dayjs().toISOString(),
          key: 'property-tax-app/3bbaba0b-be5d-49ae-bbed-6fc2b89cfb4d',
          reportUrl: null,
          updatedAt: dayjs().toISOString(),
          _id: uniqueId(),
          status: 'Processing',
        };

        if (!prev) {
          return [newReport];
        } else {
          return [newReport, ...prev];
        }
      });

      navigation('/dashboard?tab=reports');

      const response = await fetch(`${ENDPOINT}/api/v1/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch profile: ${response.statusText}`);
      }

      return (await response.json()).data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['REPORTS'] });
    },
  });
}
